// Depth-at-a-point for the walled world (B2). "Depth" used to be radial
// distance from the origin; in the generated web it becomes graph distance
// from the hub, attributed by nearest carved primitive:
//
//   in a basin  → the room's Dijkstra depth, FLAT across the whole room —
//                 a room is one biome; the blend zones live in the halls
//   in a hall   → ramp between the endpoint rooms' depths, parameterized
//                 rim-to-rim so the basin↔hall handoff is continuous
//
// Exact at room centers by construction (nodeDepth is a real shortest-path
// distance), monotone along any hub-outward swim, and immune to the
// web folding spatially (a global nearest-node min undercut far basins —
// tried and rejected, see monorail-x8w).
//
// The result is a virtual "distance from home" in world units on the same
// axis the biome/threat tables were tuned for (T.biomes radii) — every
// consumer of depthFactor / biomeFactorAt keeps its numbers.
//
// Callers go through worldDistanceAt(), which falls back to plain radial
// distance when no world layout is active (tests, cheat.dungeonOff()) or
// when the layout has no depth data (B1 mission dungeons via cheat.dungeon).

import { getActiveDungeon } from './active'
import { layoutSdf } from './carve'
import { roomRadius } from './embed'
import type { DungeonLayout } from './types'

export function graphDistanceAt(layout: DungeonLayout, x: number, y: number): number {
  const nodeDepth = layout.nodeDepth!
  let bestSurf = Infinity
  let depth = 0

  for (const disc of layout.discs) {
    const surf = Math.hypot(x - disc.x, y - disc.y) - disc.r
    if (surf < bestSurf) {
      bestSurf = surf
      depth = nodeDepth[disc.node]
    }
  }
  for (const c of layout.capsules) {
    const bax = c.bx - c.ax
    const bay = c.by - c.ay
    const pax = x - c.ax
    const pay = y - c.ay
    const len2 = bax * bax + bay * bay
    const len = Math.sqrt(len2)
    const h = len2 > 0 ? Math.max(0, Math.min(1, (pax * bax + pay * bay) / len2)) : 0
    const surf = Math.hypot(pax - bax * h, pay - bay * h) - c.r
    if (surf < bestSurf) {
      bestSurf = surf
      const e = layout.graph.edges[c.edge]
      // Rim-to-rim ramp: at either room's rim the hall matches that room's
      // flat depth, so crossing the basin↔hall handoff never jumps.
      const rA = roomRadius(layout.graph.nodes[e.a].sizeHint)
      const rB = roomRadius(layout.graph.nodes[e.b].sizeHint)
      const open = Math.max(len - rA - rB, 1e-3)
      const t = Math.max(0, Math.min(1, (h * len - rA) / open))
      depth = nodeDepth[e.a] + (nodeDepth[e.b] - nodeDepth[e.a]) * t
    }
  }
  return depth
}

/** Virtual distance-from-home at a point — graph distance in the walled
 *  world, radial distance otherwise. THE depth axis: biome bands, threat
 *  scaling, drop bonuses, music all read this. */
export function worldDistanceAt(x: number, y: number): number {
  const layout = getActiveDungeon()
  if (layout?.nodeDepth) return graphDistanceAt(layout, x, y)
  return Math.hypot(x, y)
}

/** True if the point sits in open water with `clearance` to spare. Without
 *  an active layout everything is water (the shipped open-water world). */
export function inWater(x: number, y: number, clearance = 0): boolean {
  const layout = getActiveDungeon()
  if (!layout) return true
  return layoutSdf(layout, x, y) < -clearance
}
